import React from 'react';
import type { ReactNode } from 'react';

import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@buildoutinc/blueprint-react/ui/Tooltip';

import { timelineTimeLabel, timelineTimeTitle } from 'bundles/apex/activities/timeline_helpers';

import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { Activity } from 'bundles/apex/activities/types';

interface Props {
  actions?: ReactNode;
  actors: ReactNode;
  assoc?: { label: string; path: string } | null;
  attention?: boolean;
  children?: ReactNode;
  icon: IconDefinition;
  meta?: ReactNode;
  subject?: ReactNode;
  time: Pick<Activity, 'date' | 'createdAt'>;
  trailing?: ReactNode;
}

// The contact timeline's lighter row: actor › subject line, relative time, hover actions
export default function TimelineRow({
  actions, actors, assoc, attention, children, icon, meta, subject, time, trailing,
}: Props) {
  return (
    <article className="activity-row">
      <div className="activity-row__rail">
        <span className="activity-row__icon-wrap">
          <span className={classNames('activity-row__icon', { 'activity-row__icon--attention': attention })}>
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span aria-hidden="true" className="activity-row__connector" />
      </div>

      <div className="activity-row__body">
        <div className="activity-row__head">
          <div className="activity-row__actors">
            {actors}
            <span className="activity-row__meta">
              <Tooltip>
                <Tooltip.Trigger delay={0} render={<span className="text-nowrap" />}>
                  {timelineTimeLabel(time)}
                </Tooltip.Trigger>
                <Tooltip.Content>{timelineTimeTitle(time)}</Tooltip.Content>
              </Tooltip>
              {meta}
            </span>
          </div>
          {(subject || assoc) && (
            <div className="activity-row__context">
              {subject && <span className="activity-row__subject">{subject}</span>}
              {assoc && (
                <span className="activity-row__assoc">
                  <span className="activity-row__assoc-separator">|</span>
                  <a className="activity-row__parent-link" href={assoc.path}>{assoc.label}</a>
                </span>
              )}
            </div>
          )}
        </div>

        {children}
      </div>

      {actions && <div className="activity-row__actions">{actions}</div>}

      {trailing}
    </article>
  );
}
